<template>
  <div
    id="'check_3state_' + id"
    class="rs-checkbox-3state"
    :class="getClass()"
    name="'check_3state_' + id"
  >
    <div
      class="rs-checkbox-3state__label"
      :class="{ 'no-label': !label }"
      @click="updateInput"
    >
      <div class="rs-checkbox-3state__check">
        <svg>
          <polyline points="1 5 3.5 9 10 1" />
        </svg>
      </div>
      <div class="rs-checkbox-3state__dash">
        <svg>
          <polyline points="1 5 10 5" />
        </svg>
      </div>
      <span
        v-if="label"
        class="label"
        :class="{ 'text-white': setLabelWhite }"
      >{{ label }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { threeState } from '@/shared';

// Credit: https://www.smashingmagazine.com/2017/08/creating-custom-inputs-vue-js/
export default defineComponent({
  name: 'RsFormCheckbox3state',
  props: {
    id: [String, Number],
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String as PropType<threeState>,
      default: 'none',
    },
    label: {
      type: String,
    },
    setLabelWhite: {
      type: Boolean,
      default: false,
    },

    // This allows 'some' as an induced state, but doesn't allow it to be cycled to by the Component itself.
    // This is because if the 3State is being used to control a column of checkboxes, it doesn't necessarily
    // make sense to be able to toggle *back* to the Some state from all/none, as the Some state data might
    // be destroyed by those other actions.
    allowCycleToSome: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      input: `${this.value}`,
    };
  },

  watch: {
    value() {
      this.input = `${this.value}`;
    },
  },
  methods: {
    getClass() {
      const classes = [`state_${this.value}`];
      if (this.disabled) classes.push('disabled');
      return classes.join(' ');
    },
    updateInput() {
      const currValue = this.input;
      let newValue;

      switch (currValue) {
        case 'all':
          newValue = 'none';
          break;
        case 'some':
          newValue = 'all';
          break;
        case 'none':
          newValue = this.allowCycleToSome ? 'some' : 'all';
          break;
        default:
          newValue = '';
      }

      this.input = newValue;
      this.$emit('input', newValue);
      this.$emit('change', newValue);
    },
  },

});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-checkbox-3state {
  position: relative;
  display: inline-block;
  margin: 0;

  &__label {
    display: flex;
    position: relative;
    margin: 0;
    padding-left: 20px;
    min-height: 16px;
    &:hover {
      cursor: pointer;
      &:before {
        border-color: $secondary-color;
      }
    }
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      width: 18px;
      height: 18px;
      pointer-events: none;
      background-color: #fff;
      border-radius: 3px;
      border: 1px solid $tertiary-gray;
      transition: all 0.25s ease;
    }
    &:after {
      content: '';
      position: absolute;
      top: 5px;
      left: 5px;
      width: 10px;
      height: 10px;
      border-radius: 3px;
      background: $secondary-color;
      transform: scale(0);
      transition: all .2s ease;
      opacity: .08;
      pointer-events: none;
    }

    &.no-label {
      padding-left: 0;
      width: 18px;
      height: 18px;
      &:before, &:after { top: 0; left: 0; }
    }
  }

  &__check,
  &__dash {
    display: inline-block;
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    transform: scale(1);
    vertical-align: middle;
    border-radius: 3px;
    border: 2px solid transparent;
    transform: translate3d(0,0,0);
    transition: all .2s ease;
    box-sizing: border-box;

    svg {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 10px;
      fill: none;
      stroke: $white;
      stroke-width: 2;
      stroke-dasharray: 16px;
      stroke-dashoffset: 16px;
      transition: all .3s ease;
      transition-delay: .1s;
      transform: translate3d(0,0,0);
    }
    &:before {
      content: "";
      width: 100%;
      height: 100%;
      background: $secondary-color;
      display: block;
      transform: scale(0);
      opacity: 1;
      border-radius: 50%;
    }
  }

  &.disabled{
    pointer-events: none;
    .rs-checkbox-3state__label {
      &:before{
        border-color: $tertiary-gray;
        background-color: rgba($tertiary-gray, 0.3);
      }
    }
  }

  &.state_none {
    .rs-checkbox-3state__label {
      .rs-checkbox-3state__dash,
      .rs-checkbox-3state__check {
        display: none;
      }
    }

    &.disabled{
      .rs-checkbox-3state__label {
        &:before{
          background-color: rgba($tertiary-gray, 0.3);
          border-color: $tertiary-gray;
        }
      }
    }
  }

  &.state_some {
    .rs-checkbox-3state__label {

      &:before{
        background: $secondary-color;
        border-color: $secondary-color;
        animation: wave .4s ease;
      }

      .rs-checkbox-3state__check {
        display: none;
      }
      .rs-checkbox-3state__dash {
        &:before {
          transform: scale(1.5);
          opacity: 0;
          transition: all .6s ease;
        }
        svg{
          stroke-dashoffset: 0;
        }
      }
    }

    &.disabled{
      .rs-checkbox-3state__label {
        &:before{
          background-color: rgba($tertiary-gray, 0.3);
          border-color: $tertiary-gray;
        }
        .rs-checkbox-3state__dash{
          svg{
            stroke: rgba(0,0,0,0.3);
          }
        }
      }
    }
  }

  &.state_all {
    .rs-checkbox-3state__label {

      &:before {
        background: $secondary-color;
        border-color: $secondary-color;
        animation: wave .4s ease;
      }

      .rs-checkbox-3state__check {
        &:before{
          transform: scale(1.5);
          opacity: 0;
          transition: all .6s ease;
        }
        svg{
          stroke-dashoffset: 0;
        }
      }
      .rs-checkbox-3state__dash {
        display: none;
      }
    }

    &.disabled{
      .rs-checkbox-3state__label {
        &:before{
          background-color: rgba($tertiary-gray, 0.3);
          border-color: $tertiary-gray;
        }
        .rs-checkbox-3state__check {
          svg{
            stroke: rgba(0,0,0,0.3);
          }
        }
      }
    }
  }

  @keyframes wave{
    50%{
      transform: scale(.9);
    }
  }
}
</style>
